/**
 * @file ShopIntegrationsTab.tsx
 * @description Вкладка «Интеграции» карточки магазина (phase19.28 этап А;
 * uiux-platform-shops §6; PL13). Видна с правом
 * `PLATFORM_MANAGE_INTEGRATIONS` (маршрут).
 *
 * По каждому виду: политика, источник (своя / общая платформы / нет) и
 * действие. «Перенести общую в магазин» — для «только своя» с общей
 * настройкой (она магазину не подставляется). «Подключить» / «Своя
 * настройка» открывают форму интеграции сразу для этого магазина,
 * «Изменить» — существующую. После любого действия список перечитывается.
 */

import { PlugZap } from "lucide-react";
import { useState } from "react";

import { useShopCardContext } from "../../../hooks/platform/useShopCard";
import { usePlatformResource } from "../../../hooks/platform/usePlatformResource";
import { ApiError } from "../../../lib/api-client";
import { platformIntegrations, type PlatformIntegrationDto, type PlatformIntegrationKind } from "../../../lib/api-platform-integrations";
import { platformShops, type ShopIntegrationItem } from "../../../lib/api-platform-shops";
import { isKnownKind, type ShopIntegrationAction } from "../../../lib/platform/shop-integration-meta";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { IntegrationEditDialog } from "../../../components/platform/IntegrationEditDialog";
import { ShopIntegrationCard } from "../../../components/platform/shops/ShopIntegrationCard";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { EmptyState } from "../../../components/ui/EmptyState";
import { ListError } from "../../../components/ui/ListStates";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { useNotify } from "../../../components/ui/NotifyProvider";
import { Skeleton } from "../../../components/ui/skeleton";
import { useConfirm } from "../../../components/ui/use-confirm";

/** Отказ переноса → текст (409 — у магазина уже есть своя). */
function transferError(err: unknown): string {
  if (err instanceof ApiError && err.code === "integration_shop_has_kind") {
    return "У магазина уже есть своя настройка этого вида — перенос не нужен";
  }
  return apiErrorText(err, "Не удалось перенести настройку");
}

type Editing = { kind: PlatformIntegrationKind; integration: PlatformIntegrationDto | null };

export function ShopIntegrationsTab() {
  const { shop } = useShopCardContext();
  const notify = useNotify();
  const confirmApi = useConfirm();
  const [busyKind, setBusyKind] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const list = usePlatformResource(
    `shops/${shop.id}/integrations`,
    () => platformShops.integrations(shop.id).then((res) => res.items),
    "Не удалось загрузить интеграции магазина",
  );

  async function transfer(item: ShopIntegrationItem) {
    if (!item.integrationId) return;
    const ok = await confirmApi.confirm({
      title: `Перенести общую настройку «${item.title}» в магазин «${shop.displayName}»?`,
      description: "Настройка станет своей у этого магазина и перестанет быть общей платформы. Ключи доступа переносятся вместе с ней.",
      action: "Перенести",
    });
    if (!ok) return;
    try {
      await platformShops.transferIntegration(item.integrationId, shop.id);
      notify.success(`«${item.title}» перенесена в магазин`);
      list.reload();
    } catch (err) {
      notify.error(transferError(err));
    }
  }

  async function onAction(item: ShopIntegrationItem, action: ShopIntegrationAction) {
    if (action === "transfer") return void transfer(item);
    if (!isKnownKind(item.kind)) return;
    if (action !== "edit" || !item.integrationId) {
      setEditing({ kind: item.kind, integration: null });
      return;
    }
    setBusyKind(item.kind);
    try {
      setEditing({ kind: item.kind, integration: await platformIntegrations.findOne(item.integrationId) });
    } catch (err) {
      notify.error(apiErrorText(err, "Не удалось открыть настройку"));
    } finally {
      setBusyKind(null);
    }
  }

  const items = list.data ?? [];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <NoticeStrip tone="info">
        «Только своя» — общая настройка платформы этому магазину не подставляется (перевозчики, Avito, VK, Telegram, касса).
        «Общая допустима» — работает общая, пока магазин не завёл свою.
      </NoticeStrip>
      {list.error ? <ListError message={list.error} onRetry={list.reload} /> : null}
      {list.loading && items.length === 0 ? (
        <div aria-busy="true" aria-label="Загрузка интеграций" className="grid gap-3 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      ) : null}
      {!list.loading && !list.error && items.length === 0 ? (
        <EmptyState icon={PlugZap} title="Видов интеграций нет" />
      ) : null}
      {items.length > 0 ? (
        <div className="grid min-w-0 gap-3 md:grid-cols-2">
          {items.map((item) => (
            <ShopIntegrationCard key={item.kind} item={item} busy={busyKind === item.kind} onAction={(i, a) => void onAction(i, a)} />
          ))}
        </div>
      ) : null}
      {editing ? (
        <IntegrationEditDialog
          open
          kind={editing.kind}
          integration={editing.integration}
          defaultShopId={shop.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            list.reload();
          }}
        />
      ) : null}
      <ConfirmDialog request={confirmApi.request} onSettle={confirmApi.settle} onDismiss={confirmApi.dismiss} />
    </div>
  );
}
